import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { getKpiEvaluationStyle } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { SharePresenceColleagueDayData, SharePresenceDayData } from 'src/app/models/share-presence-with-colleagues-kpi-data.model';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DayOffTypeEnum } from 'src/app/enums/day-off-type.enum';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import { HourDecimalToHoursMinutesPipe } from 'src/app/pipes/hour-decimal-to-hours-minutes.pipe';
import { KpiDetailsDialogComponent } from 'src/app/components/main/employees/performance/my-metrics/kpi-details-dialog/kpi-details-dialog.component';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import {
  DayCardColleagueData,
  DayCardData,
  DayCardTableCopy,
  DialogData,
} from './share-presence-with-colleagues-day-details-dialog.interfaces';

@Component({
  selector: 'app-share-presence-with-colleagues-day-details-dialog-component',
  imports: [CommonModule, MatIconModule, MatTooltipModule, DecimalPipe, HourDecimalToHoursMinutesPipe, KpiDetailsDialogComponent],
  templateUrl: './share-presence-with-colleagues-day-details-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './share-presence-with-colleagues-day-details-dialog.component.css',
})
export class SharePresenceWithColleaguesDayDetailsDialogComponent {
  public data = inject(MAT_DIALOG_DATA) as DialogData;

  protected readonly day_off_type = DayOffTypeEnum;

  public colleagues = computed(() => {
    const colleague_set = new Set<string>();
    this.data.presenceData.days.forEach(day => {
      day.colleagues.forEach(c => colleague_set.add(c.name));
    });
    return Array.from(colleague_set);
  });

  private today = dayjs().format('YYYY-MM-DD');

  public day_cards = computed((): DayCardData[] => {
    return this.data.presenceData.days.map(day => this.buildDayCard(day));
  });

  public subtitle = computed((): string => {
    const first_day = this.day_cards()[0]?.date;
    if (!first_day) {
      return 'Mes en curso';
    }

    const month_label = dayjs(first_day).locale('es').format('MMMM YYYY');
    return `${month_label.charAt(0).toUpperCase()}${month_label.slice(1)} - Mes en curso`;
  });

  public current_stats = computed(() => {
    const data = this.data.presenceData.current_month_to_date;
    if (!data) return null;

    const percentage = data.max_hours_possible > 0 ? (data.hours_coincided / data.max_hours_possible) * 100 : 0;
    const worked_days = this.day_cards().filter(d => !d.is_forecast).length;

    return {
      percentage,
      worked_days,
    };
  });

  public forecast_stats = computed(() => {
    const current_data = this.data.presenceData.current_month_to_date;
    const forecast_data = this.data.presenceData.next_month_forecast;

    const total_hours_coincided = (current_data?.hours_coincided ?? 0) + (forecast_data?.hours_coincided ?? 0);
    const total_max_hours = (current_data?.max_hours_possible ?? 0) + (forecast_data?.max_hours_possible ?? 0);

    const percentage = total_max_hours > 0 ? (total_hours_coincided / total_max_hours) * 100 : 0;
    const total_days = this.day_cards().length;

    return {
      percentage,
      total_days,
    };
  });

  public summary_stats = computed(() => {
    const days = this.day_cards();
    const worked_days = days.filter(d => !d.is_forecast);
    const forecast_days = days.filter(d => d.is_forecast);

    const worked_hours_total = worked_days.reduce((sum, d) => sum + d.max_hours_possible, 0);
    const worked_hours_coincided = worked_days.reduce((sum, d) => sum + d.hours_coincided, 0);

    const forecast_hours_total = forecast_days.reduce((sum, d) => sum + d.max_hours_possible, 0);
    const forecast_hours_coincided = forecast_days.reduce((sum, d) => sum + d.hours_coincided, 0);

    const total_hours = worked_hours_total + forecast_hours_total;
    const total_coincided = worked_hours_coincided + forecast_hours_coincided;
    const percentage = total_hours > 0 ? (total_coincided / total_hours) * 100 : 0;

    return {
      worked_hours_total: Math.round(worked_hours_total),
      worked_hours_coincided: Math.round(worked_hours_coincided),
      forecast_hours_total: Math.round(forecast_hours_total),
      forecast_hours_coincided: Math.round(forecast_hours_coincided),
      total_hours: Math.round(total_hours),
      total_coincided: Math.round(total_coincided),
      percentage,
    };
  });

  public will_meet_goal = computed((): boolean => {
    return this.forecast_stats().percentage >= this.data.optimal_threshold;
  });

  private buildDayCard(day: SharePresenceDayData): DayCardData {
    const date = new Date(day.date);
    const hours_coincided_rounded = Math.round(day.hours_coincided);
    const percentage =
      day.max_hours_possible <= 0
        ? 0
        : day.hours_coincided >= day.max_hours_possible
          ? 100
          : Math.min((hours_coincided_rounded / day.max_hours_possible) * 100, 100);

    const evaluation: ProductionKeyPerformanceIndicatorEvaluationEnum =
      percentage >= this.data.optimal_threshold
        ? ProductionKeyPerformanceIndicatorEvaluationEnum.Good
        : percentage >= this.data.acceptable_threshold
          ? ProductionKeyPerformanceIndicatorEvaluationEnum.Medium
          : ProductionKeyPerformanceIndicatorEvaluationEnum.Poor;

    const day_of_week = date.toLocaleDateString('es-ES', { weekday: 'long' });
    const day_of_month = date.getDate();
    const day_label = day.is_forecast
      ? `${day_of_week.charAt(0).toUpperCase() + day_of_week.slice(1)} ${day_of_month} (previsión)`
      : `${day_of_week.charAt(0).toUpperCase() + day_of_week.slice(1)} ${day_of_month}`;

    return {
      date,
      day_label,
      is_forecast: day.is_forecast,
      hours_coincided: hours_coincided_rounded,
      max_hours_possible: day.max_hours_possible,
      my_hours: day.my_hours,
      percentage,
      progress_bar_color: getKpiEvaluationStyle(evaluation).color,
      has_office_clock_registers: day.has_office_clock_registers,
      has_remote_clock_registers: day.has_remote_clock_registers,
      location_tooltip: buildLocationTooltip(day.has_office_clock_registers, day.has_remote_clock_registers, day.is_forecast),
      table_copy: buildDayCardTableCopy(day.is_forecast),
      colleagues: day.colleagues.map(
        (c: SharePresenceColleagueDayData): DayCardColleagueData => ({
          name: getShortName(c.name),
          weight: c.weight,
          is_present: c.is_present,
          coincided_hours: c.coincided_hours,
          absence_type: c.absence_type,
          absence_tooltip: c.absence_type ? buildAbsenceTooltip(c.absence_type, day.is_forecast) : null,
          potential_coincidence_hours: c.potential_coincidence_hours,
        })
      ),
    };
  }
}

function getShortName(full_name: string): string {
  const parts = full_name.split(' ');
  if (parts.length >= 2) {
    return `${parts[0]} ${parts[1].charAt(0)}.`;
  }
  return full_name;
}

function buildLocationTooltip(has_office: boolean, has_remote: boolean, is_forecast: boolean): string | null {
  if (!has_office && !has_remote) {
    return null;
  }

  if (is_forecast) {
    if (has_office) {
      return 'Tendrías presencial';
    }

    return 'Tendrías teletrabajo';
  }

  if (has_office && has_remote) {
    return 'Tienes fichajes en oficina y remoto';
  }

  if (has_office) {
    return 'Tienes fichajes en oficina';
  }

  return 'Tienes fichajes en remoto';
}

function buildDayCardTableCopy(is_forecast: boolean): DayCardTableCopy {
  return {
    day_percentage_tooltip: is_forecast
      ? '% de coincidencia ponderada prevista para ese día: horas en las que coincidirías con compañeros (según su peso) respecto a las horas de contrato del día.'
      : '% de coincidencia ponderada de ese día: horas en las que coincidiste con compañeros (según su peso) respecto a las horas de contrato del día.',
    colleague_column_tooltip: is_forecast
      ? 'Nombre del compañero. El icono junto al nombre indica su situación prevista ese día; pasa el cursor sobre él para ver el detalle.'
      : 'Nombre del compañero. El icono junto al nombre indica su situación ese día; pasa el cursor sobre él para ver el detalle.',
    present_column_label: is_forecast ? 'Coincidiría' : 'Presente',
    present_column_tooltip: is_forecast
      ? 'Sí = se prevé coincidencia (coincidiríais según horarios planificados). No = no se prevé coincidencia.'
      : 'Sí = hubo coincidencia (estuvisteis fichados a la vez al menos un rato). No = ninguna hora en común.',
    my_time_column_label: is_forecast ? 'Mi tiempo previsto' : 'Mi tiempo',
    my_time_column_tooltip: is_forecast
      ? 'Horas previstas según tu horario de contrato. Es el mismo valor para todos los compañeros de la fila.'
      : 'Horas que fichaste ese día. Es el mismo valor para todos los compañeros de la fila.',
    potential_coincidence_tooltip: is_forecast
      ? 'Máximo ponderado si estuvierais juntos todo tu tiempo previsto: Mi tiempo previsto x Peso.'
      : 'Máximo ponderado si estuvierais juntos todo tu tiempo de trabajo: Mi tiempo x Peso.',
  };
}

function buildAbsenceTooltip(absence_type: DayOffTypeEnum, is_forecast: boolean): string {
  if (is_forecast) {
    switch (absence_type) {
      case DayOffTypeEnum.Vacation:
        return 'Estará de vacaciones. No trabajará ese día: 0 coincidencia prevista y no sumará ni restará en tu porcentaje mensual.';
      case DayOffTypeEnum.Absence:
        return 'Estará ausente. No trabajará ese día: 0 coincidencia prevista y no sumará ni restará en tu porcentaje mensual.';
      case DayOffTypeEnum.Holiday:
        return 'Será festivo. Día no laborable: 0 coincidencia prevista y no sumará ni restará en tu porcentaje mensual.';
      case DayOffTypeEnum.ExcusedRemote:
        return 'Tendrá teletrabajo excusado (enfermedad/externo). No se espera en oficina: 0 coincidencia prevista y no sumará ni restará en tu porcentaje mensual.';
    }
  }

  switch (absence_type) {
    case DayOffTypeEnum.Vacation:
      return 'De vacaciones. No trabajó ese día: 0 coincidencia y no suma ni resta en tu porcentaje mensual.';
    case DayOffTypeEnum.Absence:
      return 'Ausencia. No trabajó ese día: 0 coincidencia y no suma ni resta en tu porcentaje mensual.';
    case DayOffTypeEnum.Holiday:
      return 'Festivo. Día no laborable: 0 coincidencia y no suma ni resta en tu porcentaje mensual.';
    case DayOffTypeEnum.ExcusedRemote:
      return 'Teletrabajo excusado (enfermedad/externo). No estaba en oficina: 0 coincidencia y no suma ni resta en tu porcentaje mensual.';
  }
}
